import { Controller } from "@hotwired/stimulus";

// YouTube video tracker — relies on the IFrame Player API. When the video
// reaches its end, we POST `mark_video_as_watched` so the backend can update
// the lesson progress.
//
// Activated automatically as soon as the iframe is in the DOM. We load the
// API script lazily; once ready, we attach a YT.Player to each iframe that
// has `enablejsapi=1`.
let YT_API_PROMISE = null;

function ensureYouTubeAPI() {
  if (YT_API_PROMISE) return YT_API_PROMISE;

  if (window.YT && typeof window.YT.Player === "function") {
    YT_API_PROMISE = Promise.resolve(window.YT);
    return YT_API_PROMISE;
  }

  YT_API_PROMISE = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);

    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (typeof previous === "function") previous();
      resolve(window.YT);
    };
  });
  return YT_API_PROMISE;
}

export default class extends Controller {
  static targets = ["iframe"];
  static values = {
    elementId:  String,
    progressId: String,
    juniorId:   String,
    userId:     String,
    watched:    Boolean
  };

  async connect() {
    if (this.watchedValue) return;
    if (!this.hasIframeTarget) return;

    try {
      const YT = await ensureYouTubeAPI();
      this.player = new YT.Player(this.iframeTarget, {
        events: {
          onStateChange: (event) => this._onStateChange(event)
        }
      });
    } catch (_e) { /* ignored */ }
  }

  disconnect() {
    if (this.player && typeof this.player.destroy === "function") {
      try { this.player.destroy(); } catch (_e) { /* noop */ }
    }
  }

  _onStateChange(event) {
    if (this.watchedValue) return;
    // 0 === ended
    if (event.data === 0) this._markWatched();
  }

  async _markWatched() {
    if (this.watchedValue) return;
    this.watchedValue = true;

    if (!this.progressIdValue || this.progressIdValue === "") return;
    try {
      const response = await fetch(`/juniors/${this.juniorIdValue}/lesson_progresses/${this.progressIdValue}/mark_video_as_watched`, {
        method: "POST",
        headers: this._headers(),
        credentials: "same-origin",
        body: JSON.stringify({ lesson_element_id: this.elementIdValue })
      });
      if (!response.ok) {
        // Le serveur n'a rien enregistre : on rouvre la porte pour un nouvel essai.
        this.watchedValue = false;
        return;
      }
      this.element.dispatchEvent(new CustomEvent("lc-training-element:progress-changed", {
        bubbles: true,
        detail: { reason: "video-watched" }
      }));
    } catch (_e) {
      // Coupure reseau : on retentera au prochain passage en fin de video.
      this.watchedValue = false;
    }
  }

  _headers() {
    const csrf = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || "";
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrf,
      Accept: "application/json"
    };
  }
}
